import { zodResolver } from '@hookform/resolvers/zod'
import {
	type Residence,
	type ResidenceData,
	residenceUpdate,
	residenceUpdateLocation
} from '@repo/sdk/gen'
import {
	zResidenceData,
	zResidenceLocation,
	zResidenceSiteData
} from '@repo/sdk/zod'
import type { Resolver } from 'react-hook-form'
import { z } from 'zod'
import i18n from '@/lib/i18n'
import { euro } from './residence-errors'

/**
 * The form holds the data as the API takes it, and for each site where it is on the map:
 * the address field sets it when a suggestion is picked, and it has its own call, so it
 * never goes with the data.
 */
const zResidenceForm = zResidenceData.extend({
	sites: z
		.array(
			zResidenceSiteData.extend({
				location: zResidenceLocation.nullable().optional()
			})
		)
		.max(3)
})
export type ResidenceFormData = z.infer<typeof zResidenceForm>

/** A new site: counts at zero, everything optional left empty. */
export const EMPTY_SITE: ResidenceData['sites'][number] = {
	name: '',
	address: '',
	city: '',
	typology: null,
	singleBeds: 0,
	doubleBeds: 0,
	bathroomsCount: null,
	bathroomsDescription: null,
	kitchensCount: null,
	kitchensDescription: null,
	otherServicesDescription: null,
	equippedSingleBeds: 0,
	equippedDoubleBeds: 0,
	equippedBathroomsDescription: null,
	equippedKitchensDescription: null,
	equippedOtherServicesDescription: null
}

/** A seat count as a whole number: a field still being typed is not a number yet. */
export const seatCount = (value: number) =>
	Number.isInteger(value) ? value : 0

/**
 * The seats of the residence: those of its sites, of which the manager gives
 * the quota 30% and the rest is the quota 70%. The quota 70% is never typed,
 * so the sites and the quotas cannot disagree.
 */
export function seatsOf(
	data: Pick<ResidenceData, 'sites' | 'q30SingleBeds' | 'q30DoubleBeds'>
) {
	const single = data.sites.reduce(
		(sum, site) => sum + seatCount(site.singleBeds),
		0
	)
	const double = data.sites.reduce(
		(sum, site) => sum + seatCount(site.doubleBeds),
		0
	)
	const q30Single = seatCount(data.q30SingleBeds)
	const q30Double = seatCount(data.q30DoubleBeds)
	return {
		single,
		double,
		q30Single,
		q30Double,
		q70Single: single - q30Single,
		q70Double: double - q30Double
	}
}

const schemaResolver = zodResolver(zResidenceForm, {
	error: residenceIssueMessage
})

/**
 * The schema's resolver on the data with the quota 70% seats worked out from
 * the sites: they are what the sites leave to the quota 30%, so what is saved
 * always adds up. A quota 30% above the seats of the sites leaves none for the
 * quota 70%: it saves all the same (decision D73) and the API lists the sum
 * that does not match.
 */
export const residenceResolver: Resolver<ResidenceFormData> = (
	values,
	context,
	options
) => {
	const seats = seatsOf(values)
	return schemaResolver(
		{
			...values,
			q70SingleBeds: Math.max(0, seats.q70Single),
			q70DoubleBeds: Math.max(0, seats.q70Double)
		},
		context,
		options
	)
}

/** The editable data of a residence, without its id, call and counters, and where each site is on the map. */
export function dataOf(residence: Residence): ResidenceFormData {
	// Object.fromEntries loses the key types; the keys are exactly ResidenceData's.
	const data = Object.fromEntries(
		Object.keys(zResidenceData.shape).map((key) => [
			key,
			residence[key as keyof ResidenceData]
		])
	) as Omit<ResidenceData, 'sites'>
	// The position in the list is the order.
	return {
		...data,
		sites: residence.sites.map(({ position: _position, ...site }) => site)
	}
}

/** The data as `residence/update` takes it: the sites without where they are on the map, which has its own call. */
const apiDataOf = (data: ResidenceFormData): ResidenceData => ({
	...data,
	sites: data.sites.map(({ location: _location, ...site }) => site)
})

/**
 * Saves what the form holds: the fields that changed with `residence/update`, then the
 * position of each site whose address was picked or changed (the server keeps the sites in
 * the order sent, so the site at an index is the form's). Resolves to whether anything was
 * saved; throws the API error of the first call that fails.
 */
export async function saveResidence(
	residence: Residence,
	data: ResidenceFormData
) {
	const changes = changedData(apiDataOf(dataOf(residence)), apiDataOf(data))
	const hasChanges = Object.keys(changes).length > 0
	const saved = hasChanges
		? (
				await residenceUpdate({
					body: { data: { id: residence.id, ...changes } }
				})
			).data
		: residence
	let moved = 0
	for (const [index, site] of data.sites.entries()) {
		const location = site.location ?? null
		const stored = saved.sites[index]
		if (
			stored &&
			JSON.stringify(location) !== JSON.stringify(stored.location)
		) {
			await residenceUpdateLocation({
				body: {
					data: { id: residence.id, siteId: stored.id, location }
				}
			})
			moved += 1
		}
	}
	return hasChanges || moved > 0
}

/** Only the fields that changed, for `residence/update`. */
export function changedData(
	before: ResidenceData,
	after: ResidenceData
): Partial<ResidenceData> {
	return Object.fromEntries(
		Object.entries(after).filter(
			([key, value]) =>
				JSON.stringify(value) !==
				JSON.stringify(before[key as keyof ResidenceData])
		)
	)
}

/**
 * The message of a schema issue, said in the admin's words: what is missing
 * and how to fix it. Unions with `null` report their first, non-null branch.
 */
export function residenceIssueMessage(
	issue: z.core.$ZodRawIssue
): string | undefined {
	if (issue.code === 'invalid_union') {
		return issue.errors[0]?.[0]?.message
	}
	if (issue.code === 'invalid_type') {
		return i18n.t(
			issue.expected === 'number' || issue.expected === 'int'
				? 'admin-residences:form.number_required'
				: 'admin-residences:form.required'
		)
	}
	if (issue.code === 'too_small') {
		return issue.origin === 'string'
			? i18n.t('admin-residences:form.required')
			: i18n.t('admin-residences:form.minimum', {
					minimum: Number(issue.minimum)
				})
	}
	if (issue.code === 'too_big' && isCentsField(issue.path?.[0])) {
		return i18n.t('admin-residences:form.rate_maximum', {
			maximum: euro(Number(issue.maximum))
		})
	}
	if (issue.code === 'too_big') {
		return issue.origin === 'string'
			? i18n.t('admin-residences:form.too_long', {
					maximum: Number(issue.maximum)
				})
			: i18n.t('admin-residences:form.maximum', {
					maximum: Number(issue.maximum)
				})
	}
	if (issue.code === 'invalid_format') {
		if (issue.format === 'email') {
			return i18n.t('admin-residences:form.email_invalid')
		}
		if (issue.format === 'url') {
			return i18n.t('admin-residences:form.url_invalid')
		}
		return i18n.t('admin-residences:form.format_invalid')
	}
	return undefined
}

/** Rates are integer cents: their limits read as euro amounts. */
function isCentsField(field: PropertyKey | undefined) {
	return typeof field === 'string' && field.endsWith('RateCents')
}
